/**
 * Mountain Brook's pink permit application as a PDF file (ADR 0289).
 *
 * Drawn in the browser from the form the office checked, so what lands in
 * Docs is exactly what was on the screen when they pressed Save. One page,
 * letter size, painted pink like the paper.
 */

import type { PermitApplication } from "@roof-hawk/core";

import {
  PAGE_HEIGHT,
  PAGE_WIDTH,
  Pen,
  inks,
  loadFonts,
  type PdfLib,
} from "./kit";
import { drawPinkSheet } from "./pink";

export async function permitPdf(
  lib: PdfLib,
  form: PermitApplication,
): Promise<Uint8Array> {
  const doc = await lib.PDFDocument.create();
  doc.setTitle("Building Permit Application");
  const fonts = await loadFonts(lib, doc);
  const ink = inks(lib);
  const page = doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  const pen = new Pen(page, fonts, ink, 0);
  pen.fill(0, 0, PAGE_WIDTH, PAGE_HEIGHT, ink.pink);
  drawPinkSheet(pen, form);
  return doc.save();
}

/**
 * The PDF as a file to upload, with pdf-lib loaded only now: nobody opening
 * a job pays for a library only the Permit step uses.
 */
export async function permitPdfFile(
  form: PermitApplication,
  name: string,
): Promise<File> {
  const lib = (await import("pdf-lib")) as unknown as PdfLib;
  const bytes = await permitPdf(lib, form);
  return new File([bytes as BlobPart], name, { type: "application/pdf" });
}
